import React, { useCallback, useEffect, useState } from 'react';
import { Play, X } from 'lucide-react';
import {
  actionLabels,
  eventActionLabels,
  musicLinksRelease,
  youtubeEmbedUrl,
  youtubeThumbnail,
} from '../data/musicLinks';

const MusicLinks: React.FC = () => {
  const { title, subtitle, youtubeVideoId, artworkUrl, links, events } = musicLinksRelease;
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const hasVideo = Boolean(youtubeVideoId);
  const artwork =
    artworkUrl ?? (hasVideo ? youtubeThumbnail(youtubeVideoId) : null);

  const closeVideo = useCallback(() => setIsVideoOpen(false), []);

  useEffect(() => {
    if (!isVideoOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeVideo();
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isVideoOpen, closeVideo]);

  return (
    <div className="min-h-svh bg-black text-white">
      <div className="mx-auto flex min-h-svh max-w-[440px] flex-col items-center px-5 py-10 sm:py-14">
        <div className="w-48">
          {hasVideo ? (
            <button
              type="button"
              onClick={() => setIsVideoOpen(true)}
              className="group relative aspect-square w-full overflow-hidden rounded-sm bg-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              aria-label="Play video"
            >
              {artwork ? (
                <img
                  src={artwork}
                  alt=""
                  className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                />
              ) : null}
              <span className="absolute inset-0 bg-black/25 transition group-hover:bg-black/35" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/80 bg-black/40 backdrop-blur-sm transition group-hover:scale-105 group-hover:bg-black/55">
                  <Play className="ml-1 h-7 w-7 fill-white text-white" aria-hidden />
                </span>
              </span>
            </button>
          ) : (
            <div className="relative aspect-square w-full overflow-hidden rounded-sm bg-zinc-900">
              {artwork ? (
                <img src={artwork} alt="" className="h-full w-full object-cover" />
              ) : null}
            </div>
          )}
        </div>

        <header className="mt-8 w-full text-center">
          <h1 className="font-serif text-3xl leading-tight tracking-tight sm:text-4xl">
            {title}
          </h1>
          {subtitle ? (
            <p className="mt-2 text-sm uppercase tracking-[0.2em] text-zinc-400">
              {subtitle}
            </p>
          ) : null}
        </header>

        <ul className="mt-4 flex w-full flex-col gap-2">
          {links.map((link) => (
            <li key={link.label}>
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between rounded-sm border border-zinc-700 bg-transparent px-4 py-3.5 text-sm transition hover:border-zinc-500 hover:bg-zinc-950"
              >
                <span className="font-medium tracking-wide">{link.label}</span>
                <span className="text-xs uppercase tracking-wider text-zinc-400">
                  {actionLabels[link.action]}
                </span>
              </a>
            </li>
          ))}
        </ul>

        {events.length > 0 ? (
          <>
            <p className="mt-10 w-full text-center text-xs uppercase tracking-[0.25em] text-zinc-500">
              Upcoming Events
            </p>
            <ul className="mt-4 flex w-full flex-col gap-2">
              {events.map((event) => (
                <li key={event.label}>
                  <a
                    href={event.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between rounded-sm border border-zinc-700 bg-transparent px-4 py-3.5 text-sm transition hover:border-zinc-500 hover:bg-zinc-950"
                  >
                    <span className="font-medium tracking-wide">{event.label}</span>
                    <span className="text-xs uppercase tracking-wider text-zinc-400">
                      {eventActionLabels[event.action]}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>

      {isVideoOpen && hasVideo ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-label={`${title} video`}
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/85 backdrop-blur-sm"
            onClick={closeVideo}
            aria-label="Close video"
          />
          <div className="relative z-10 w-full max-w-3xl">
            <button
              type="button"
              onClick={closeVideo}
              className="absolute -top-12 right-0 flex h-10 w-10 items-center justify-center rounded-full border border-zinc-600 text-zinc-300 transition hover:border-zinc-400 hover:text-white"
              aria-label="Close video"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
            <div className="relative aspect-video w-full overflow-hidden rounded-sm bg-black shadow-2xl">
              <iframe
                title={`${title} video`}
                src={youtubeEmbedUrl(youtubeVideoId)}
                className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MusicLinks;
